'use client';

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState, useCallback, useRef, Suspense } from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Turnstile, type TurnstileRef } from '@/components/turnstile';

const TURNSTILE_CONFIGURED = !!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const from = searchParams.get('from') ?? '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined);
  const turnstileRef = useRef<TurnstileRef>(null);

  const handleTurnstileToken = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(undefined);
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken(undefined);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (TURNSTILE_CONFIGURED && !turnstileToken && process.env.NODE_ENV !== 'development') {
      setError('Bitte warten Sie auf die Sicherheitsprüfung.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, turnstileToken: turnstileToken ?? undefined }),
      });

      const data = (await res.json()) as { error?: string };

      if (!res.ok) {
        setError(data.error ?? 'Anmeldung fehlgeschlagen.');
        // Reset Turnstile so a fresh challenge token is issued
        setTurnstileToken(undefined);
        turnstileRef.current?.reset();
        return;
      }

      window.location.href = from;
    } catch {
      setError('Netzwerkfehler. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card className="w-full max-w-md card-shadow-lg border-border">
      <CardHeader className="text-center pb-2">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold mb-3">
          SB
        </div>
        <CardTitle className="text-xl">Willkommen zurück</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Melden Sie sich bei salesbriefing.de an
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">E-Mail</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@firma.de"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Passwort</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Link href="/forgot-password" className="text-xs text-accent hover:underline">
              Passwort vergessen?
            </Link>
          </div>

          <Turnstile
            ref={turnstileRef}
            onToken={handleTurnstileToken}
            onExpire={handleTurnstileExpire}
            onError={handleTurnstileError}
          />

          <Button type="submit" variant="accent" className="w-full" disabled={loading}>
            {loading ? 'Wird angemeldet…' : 'Anmelden'}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            Noch keinen Account?{' '}
            <Link href="/register" className="text-accent font-medium hover:underline">
              Jetzt registrieren
            </Link>
          </p>
        </form>
      </CardContent>
    </Card>
  );
}

export default function LoginPage() {
  return (
    <div className="flex min-h-[calc(100vh-10rem)] items-center justify-center p-4">
      <Suspense>
        <LoginForm />
      </Suspense>
    </div>
  );
}
